/* 匯入管線：檔案進來 → 指紋 → 解析 → 提煉 → 草稿。
   三條規矩：
   1. **原檔不落地**。我們只留下指紋與提煉後的概念，原始位元組用完就丟。
      你磁碟上的原檔我們也不會碰。
   2. **指紋永久保留**：同一份文件第二次丟進來會被擋下，不會產生第二套草稿。
   3. 提煉出來的一律是**草稿**：沒有採集標籤就不算數，但你可以先收著再補。 */
import type { Category, Concept, ImportFingerprint } from "../domain/types.ts";
import type { Repository } from "../data/repository.ts";
import { parseFile } from "./parsers.ts";
import { extractConcepts, suggestCategory } from "./extract.ts";
import type { CategorySuggestion } from "./extract.ts";

export interface ImportInput {
  name: string;
  type?: string;
  size?: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export interface ImportContext {
  categories: Category[];
  concepts: Concept[];
  adultUnlocked: boolean;
  now?: number;
  maxConcepts?: number;
}

export interface ImportResult {
  fileName: string;
  status: "imported" | "duplicate" | "empty" | "failed";
  concepts: number;
  created: Concept[];
  suggestion: CategorySuggestion | null;
  duplicateOf?: string;
  error?: string;
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function importFile(
  repo: Repository, file: ImportInput, ctx: ImportContext,
): Promise<ImportResult> {
  const now = ctx.now ?? Date.now();
  const base: ImportResult = {
    fileName: file.name, status: "failed", concepts: 0, created: [], suggestion: null,
  };
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch (e) {
    return { ...base, error: "讀不到檔案內容：" + (e instanceof Error ? e.message : String(e)) };
  }

  const hash = await sha256Hex(bytes);
  const seen = await repo.getFingerprint(hash);
  if (seen) {
    return { ...base, status: "duplicate", duplicateOf: seen.fileName };
  }

  try {
    const doc = await parseFile({ name: file.name, type: file.type,
      arrayBuffer: async () => bytes.buffer as ArrayBuffer });
    const extracted = extractConcepts(doc, { max: ctx.maxConcepts ?? 60 });
    if (!extracted.length) {
      /* 抽不到東西也要記指紋 —— 否則同一份檔案會一直被重試。 */
      await repo.saveFingerprint({ sha256: hash, fileName: file.name, importedAt: now, conceptIds: [] });
      return { ...base, status: "empty", error: "這份文件抽不到足夠的內容（掃描件需要 OCR，那在桌面端）。" };
    }

    const created: Concept[] = [];
    let suggestion: CategorySuggestion | null = null;
    for (const item of extracted) {
      const text = item.summary + " " + item.body;
      const guess: CategorySuggestion | null =
        suggestion ?? suggestCategory(text, ctx.categories, ctx.concepts, ctx.adultUnlocked);
      if (!suggestion) suggestion = guess;
      const categoryId = guess?.categoryId ?? "cat-uncategorized";
      const res = await repo.saveConcept(null, {
        title: item.title, summary: item.summary, body: item.body,
        note: "", source: item.sourceRef, primaryCategoryId: categoryId,
        status: "draft", contentHash: hash,
      }, "import");
      created.push(res.concept);
    }

    const fingerprint: ImportFingerprint = {
      sha256: hash, fileName: file.name, importedAt: now, conceptIds: created.map((c) => c.id),
    };
    await repo.saveFingerprint(fingerprint);
    return { ...base, status: "imported", concepts: created.length, created, suggestion };
  } catch (e) {
    return { ...base, error: e instanceof Error ? e.message : String(e) };
  }
}
